import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, TrendingUp, Sparkles, CheckCircle2, Clock, BarChart3, Info } from "lucide-react";
import { Card, ActivityFeedSection, CoachActivityTrendSection, TestimonialManager } from "@/pages/MarketingDashboard";

const FUNNEL_DEFS = {
  sent: "Emails handed to the provider for this campaign (invitation sent).",
  clicked: "Recipient clicked their claim link at least once. A click is NOT a claim.",
  claim_link_clicked: "Clicked through but has not created an account yet.",
  accounts_created: "Coach completed signup and verified ownership — the primary conversion metric.",
  unsubscribed: "Recipient opted out of all future emails.",
  declined: "Recipient explicitly declined their profile.",
};

export default function AdminInsights() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    api.get("/admin/marketing-stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/campaign-stats").then(({ data }) => setFunnel(data)).catch(() => {});
  }, []);

  return (
    <div className="text-white">
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="admin-insights-page">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><BarChart3 size={14} /> ADMIN HQ · FUNNEL &amp; ANALYTICS</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">FUNNEL &amp; ANALYTICS</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Read-only health of the platform. Operating controls live in Campaigns, Work Queue and System.
        </p>

        {/* Funnel cards — corrected claim terminology */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="funnel-cards">
            <Card icon={Users} label="Total profiles" value={stats.tkg.total_profiles} />
            <Card icon={Users} label="Scouted (unclaimed)" value={stats.tkg.scouted_unclaimed} accent="blaze" />
            <a href="/hq/coaches?claim_state=auto" data-testid="clicked-card-link" className="block hover:opacity-80 transition-opacity"
              title={FUNNEL_DEFS.claim_link_clicked}>
              <Card icon={Sparkles} label="Claim link clicked ↗" value={stats.tkg.soft_claimed ?? 0} />
            </a>
            <a href="/hq/coaches?claim_state=account" data-testid="accounts-card-link" className="block hover:opacity-80 transition-opacity"
              title={FUNNEL_DEFS.accounts_created}>
              <Card icon={CheckCircle2} label="Accounts created ↗" value={stats.tkg.account_claimed ?? stats.tkg.claimed} accent="volt" />
            </a>
          </div>
        )}

        {/* Campaign conversion funnel with metric definitions */}
        {funnel && (
          <div className="mt-4 border border-neutral-800 bg-appcard p-4" data-testid="campaign-funnel-panel">
            <div className="text-[10px] tracking-widest text-neutral-500 heading flex items-center gap-1.5">
              CONVERSION FUNNEL · INVITATION SENT → CLAIM LINK CLICKED → ACCOUNT CREATED
              <span title="Hover any metric for its exact definition. The primary conversion metric is accounts created — not link clicks."><Info size={11} className="text-neutral-600" /></span>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: "coach_claim", label: "COACH PROFILE CLAIM INVITATIONS" },
                { key: "hc_pitch", label: "HC PITCHES" },
                { key: "vacancy_outreach", label: "VACANCY LISTING OUTREACH" },
              ].map(({ key, label }) => {
                const s = funnel[key] || {};
                const accounts = s.claimed ?? 0;
                const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
                return (
                  <div key={key} className="border border-neutral-900 p-3" data-testid={`funnel-${key}`}>
                    <div className="text-[10px] tracking-widest text-neutral-400 heading">{label}</div>
                    <div className="mt-2 flex items-end gap-3 flex-wrap">
                      <Metric label="SENT" value={s.sent ?? 0} def={FUNNEL_DEFS.sent} />
                      {s.opened != null && (
                        <Metric label={`CLICKED ${pct(s.opened, s.sent)}`} value={s.opened} def={FUNNEL_DEFS.clicked} color="text-sky-400" />
                      )}
                      {(s.soft_claimed ?? 0) > 0 && (
                        <Metric label="LINK CLICKED, NO ACCOUNT" value={s.soft_claimed} def={FUNNEL_DEFS.claim_link_clicked} color="text-sky-400" />
                      )}
                      <Metric label={`ACCOUNTS ${pct(accounts, s.sent)}`} value={accounts} def={FUNNEL_DEFS.accounts_created} color="text-volt" />
                      {(s.declined ?? 0) > 0 && <Metric label="DECLINED" value={s.declined} def={FUNNEL_DEFS.declined} color="text-blaze" />}
                      {(s.unsubscribed ?? 0) > 0 && <Metric label="UNSUB" value={s.unsubscribed} def={FUNNEL_DEFS.unsubscribed} color="text-blaze" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Fill-rate tracker */}
        {stats?.jobs && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fill-rate-cards">
            <Card icon={TrendingUp} label="Open jobs" value={stats.jobs.open} accent="volt" />
            <Card icon={CheckCircle2} label="Jobs filled" value={stats.jobs.filled} accent="blaze" sub={`OF ${stats.jobs.total_posted} POSTED`} />
            <Card icon={TrendingUp} label="Fill rate" value={`${stats.jobs.fill_rate_pct ?? 0}%`} />
            <Card icon={Clock} label="Avg days to fill" value={stats.jobs.avg_days_to_fill ?? "—"} />
          </div>
        )}

        {/* Top sources */}
        {stats && Object.keys(stats.sources || {}).length > 0 && (
          <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="top-sources">
            <div className="text-xs tracking-widest text-volt">PROFILES BY SOURCE</div>
            <div className="mt-3 space-y-1">
              {Object.entries(stats.sources).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm border border-neutral-800 px-3 py-2">
                  <span className="text-neutral-300">{k}</span>
                  <span className="heading text-volt">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ActivityFeedSection readOnly />

        <CoachActivityTrendSection readOnly />

        <TestimonialManager />
      </div>
    </div>
  );
}

function Metric({ label, value, def, color = "" }) {
  return (
    <div title={def} className="cursor-help" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`heading text-2xl ${color}`}>{value}</div>
      <div className="text-[9px] tracking-widest text-neutral-600 flex items-center gap-0.5">{label} <Info size={8} /></div>
    </div>
  );
}
